import { useEffect, memo, useCallback } from 'react'
import { observer } from 'mobx-react'

import { useSaveButton } from 'hooks/useSaveButton'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { useStores } from 'stores/utils/hooks/useStores'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import {
  CONFIG_DDF,
  CONFIG_SECOND_DEBTOR,
  isFFGConfigWithAutoRedirectOnDacSuccess
} from 'config/utils/config'
import { NavigateFunction } from 'react-router-dom'
import LegalHintTexts from 'texts/common/copy/legal'
import {
  RegisteredEventName,
  broadcastToParent
} from '@finanzcheck/catalyst-pollard'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { getOptimizelyTrackingAttribute } from 'utils/tracking'
import { usePayoutAccountValidation } from 'hooks/usePayoutAccountValidation'
import { getButtonSizes } from './getButtonSizes'
import FormNavigation from './FormNavigationTemplate'
import { getAge } from 'utils/date'
import { AgeGroup } from 'constants/components'
import { useAgeGroup } from 'stores/utils/hooks/useAgeGroup'
import { Button } from '@mui/material'
import { Experiments } from 'CustomOptimizelyProvider'
import { PossibleVariants } from 'utils/tracking/optimizely/types'
import { CONFIG_DDF_RCE_DAC_CHOICE } from 'config/utils/config'
import { isYoungOrMiddleAgeGroup } from 'stores/helpers/AgeGroups'

type Props = {
  onBack?: () => void
  onNext: () => void
  legalContact: string
  navigate: NavigateFunction
}

const FFGFormNavigation = observer(({ onBack, onNext, navigate }: Props) => {
  const {
    rootStore: {
      isInIframe,
      urlParams: { backUrl },
      traversal: {
        data: {
          common: { amount },
          debtors: {
            primary: {
              personal: { birthday }
            }
          }
        }
      },
      experimentData,
      page: { config }
    }
  } = useRootStore()

  const {
    page: { isFirstDisplayedPage, isLastDisplayedPage, getCurrentIndex },
    navigation: { currentPageInSession },
    traversal: {
      data,
      dacWidgetState,
      conditionBasedDacDescision,
      conditionBasedDacDescisionHasErrorState,
      setconditionBasedDacDescisionHasErrorState
    }
  } = useStores()

  const ageGroup = useAgeGroup()
  const debtorsAge = getAge(birthday)
  const isYoungAgeConditionMet =
    ageGroup === AgeGroup.YOUNG_AGE_GROUP ||
    ageGroup === AgeGroup.TOO_YOUNG_AGE_GROUP

  const isPayoutAccountReady = usePayoutAccountValidation(true)

  const { isSaveButtonVisible } = useSaveButton({
    onSuccess: () => {},
    onError: () => {}
  })

  const currentIndex = getCurrentIndex()

  const trackWithOrigin = (event: AmplitudeEvent, payload = {}) => {
    trackInAmplitude(event, { origin: 'taurine', ...payload })
  }

  const isInCat3744Variation =
    experimentData[Experiments.CAT_3744_DAC_HIGH_VALUE_CUSTOMER] ===
      PossibleVariants.VARIATION_B &&
    amount >= 30000 &&
    isYoungOrMiddleAgeGroup(debtorsAge)

  const isInCAT4085ColorChange =
    experimentData[Experiments.CAT_4085_COLOR_CHANGE] ===
    PossibleVariants.VARIATION_B

  const isInCat3818Variation = config.name === CONFIG_DDF_RCE_DAC_CHOICE

  const onNextWithTracking = useCallback(() => {
    const isInCat3744VariationError =
      isInCat3744Variation &&
      conditionBasedDacDescision === undefined &&
      isLastPage

    if (isInCat3744VariationError) {
      setconditionBasedDacDescisionHasErrorState(true)
      return
    }

    const isInCat3818VariationError =
      isInCat3818Variation &&
      conditionBasedDacDescision === undefined &&
      isLastPage &&
      isYoungOrMiddleAgeGroup(debtorsAge) // we only check the dac decision error for young and middle age groups

    if (isInCat3818VariationError) {
      setconditionBasedDacDescisionHasErrorState(true)
      return
    }
    onNext()
  }, [onNext, data, isInCat3744Variation, conditionBasedDacDescision])

  useEffect(() => {
    if (
      isInCat3818Variation &&
      !conditionBasedDacDescisionHasErrorState &&
      typeof conditionBasedDacDescision !== 'undefined' &&
      !conditionBasedDacDescision
    ) {
      onNextWithTracking()
    }
  }, [
    isInCat3818Variation,
    conditionBasedDacDescision,
    conditionBasedDacDescisionHasErrorState,
    onNextWithTracking
  ])

  const onBackHandler = () => {
    const isFinalizingOnBackButton =
      dacWidgetState !== 'initial' &&
      isLastPage &&
      ageGroup !== AgeGroup.OLD_AGE_GROUP &&
      !isInCat3744Variation
    if (isLastPage) {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'back_button_click_on_dac_page',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
    }
    if (isFinalizingOnBackButton) {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'final_on_back_button_click',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
      onNext()
      return
    }
    onBack && onBack()
  }

  const isLastPage = isLastDisplayedPage(currentPageInSession)

  useEffect(() => {
    if (
      (isYoungAgeConditionMet ||
        isFFGConfigWithAutoRedirectOnDacSuccess(config.name)) &&
      dacWidgetState === 'success'
    ) {
      trackWithOrigin(AmplitudeEvent.OnDACSuccessAutoRedirect)
      onNextWithTracking()
    }
  }, [dacWidgetState, config.name])

  const isFirstPage = isFirstDisplayedPage(currentPageInSession)

  const backLabel =
    !isInIframe || currentIndex > 0 || backUrl ? 'Zurück' : undefined

  const isBackButtonHidden =
    !backLabel || isFirstPage || (isInCat3818Variation && isLastPage)

  let submitLabel = isLastPage ? 'Angebote ansehen' : 'Weiter'

  const getIsContinueButtonHidden = () => {
    if (isInCat3744Variation && !conditionBasedDacDescision) {
      submitLabel = 'Weiter'
      return false
    }

    if (isInCat3818Variation && isLastPage && !conditionBasedDacDescision) {
      submitLabel = 'Weiter zu den Angeboten'
      return false
    }

    if (config.name === CONFIG_SECOND_DEBTOR) {
      return false
    }

    if (config.name === CONFIG_DDF) {
      if (
        (currentPageInSession === 'dac' && dacWidgetState !== 'success') ||
        (ageGroup !== AgeGroup.OLD_AGE_GROUP && isLastPage)
      ) {
        return true
      }

      return false
    }

    return (
      isLastPage &&
      isPayoutAccountReady &&
      dacWidgetState !== 'success' &&
      isYoungOrMiddleAgeGroup(debtorsAge)
    )
  }

  const isContinueButtonHidden = getIsContinueButtonHidden()

  const { sizeOfContinueButton, sizeOfBackButton, sizeOfSaveButton } =
    getButtonSizes(
      isBackButtonHidden,
      isSaveButtonVisible,
      isContinueButtonHidden
    )

  const showSkipDacButton =
    ageGroup === AgeGroup.MIDDLE_AGE_GROUP &&
    isLastPage &&
    !isInCat3744Variation &&
    !isInCat3818Variation

  const showDacLegalText =
    isLastPage &&
    isPayoutAccountReady &&
    currentPageInSession === 'dac' &&
    !isInCat3818Variation

  return (
    <FormNavigation
      onBack={onBackHandler}
      onNext={onNext}
      navigate={navigate}
      legalText={LegalHintTexts.defaultLegalHint}
      isContinueWithoutDacVisible={false}
      isBackButtonHidden={isBackButtonHidden}
      isContinueButtonHidden={isContinueButtonHidden}
      sizeOfContinueButton={showSkipDacButton ? 12 : sizeOfContinueButton}
      sizeOfBackButton={sizeOfBackButton}
      sizeOfSaveButton={showSkipDacButton ? 12 : sizeOfSaveButton}
      submitLabel={submitLabel}
      backLabel={backLabel}
      onNextWithTracking={onNextWithTracking}
      trackWithOrigin={trackWithOrigin}
      continueButtonColor={isInCAT4085ColorChange ? 'primary' : 'secondary'}
      continueWithoutDacButtonVariant={'text'}
      skipDacButton={
        showSkipDacButton ? (
          <Button
            size="large"
            type="button"
            id="test-submit"
            name="test-submit"
            onClick={onNextWithTracking}
            color="primary"
            fullWidth
            variant="contained"
          >
            Ich habe keinen Online-Banking-Zugang
          </Button>
        ) : null
      }
      showDacLegalText={showDacLegalText}
      backButtonVariant="text"
      saveButtonVariant={isInCAT4085ColorChange ? 'outlined' : 'contained'}
    />
  )
})

export default memo(FFGFormNavigation)
